import { CategoryEnum } from "@boundless/bl-api-client/common/models"
import {
  getCustomerCompanyRetrieveQueryKey,
  useCustomerApplicationsRetrieveSuspense,
  useCustomerCompanyPartialUpdate,
  useCustomerCompanyRetrieveSuspense,
  useCustomerLendersRetrieveSuspense,
  useCustomerRepositoryListSuspense,
} from "@boundless/bl-api-client/customer/services"
import { createFileRoute, redirect } from "@tanstack/react-router"

import { RequiredDocumentsForm } from "@/components/applications/upload-documents/RequiredDocumentsForm"
import { ButtonBack } from "@/components/buttons/ButtonBack"
import { ButtonNext } from "@/components/buttons/ButtonNext"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Separator } from "@/components/ui/separator"
import { ApplicationFlowContext } from "@/lib/flows/application"
import { getExtraDataFormComponent } from "@/lib/integrations"
import { cn } from "@/lib/utils"

import type {
  RepositoryDocument,
  RequiredDocumentDetails,
} from "@boundless/bl-api-client/customer/models"

export const Route = createFileRoute(
  "/_dashboard/_application-flow/applications/$applicationUuid/upload-documents",
)({
  component: RouteComponent,
  beforeLoad: ({ context: { store } }) => {
    const companyUuid = store.getState().activeCompanyUuid
    if (!companyUuid) {
      throw redirect({ to: "/home" })
    }
    return { companyUuid }
  },
})

function RouteComponent() {
  const flowActorRef = ApplicationFlowContext.useActorRef()
  const { applicationUuid } = Route.useParams()
  const { companyUuid, queryClient } = Route.useRouteContext()
  const companyQueryKey = getCustomerCompanyRetrieveQueryKey(companyUuid)

  const { data: application, queryKey: applicationQueryKey } =
    useCustomerApplicationsRetrieveSuspense(companyUuid, applicationUuid)

  const { data: lender } = useCustomerLendersRetrieveSuspense(
    companyUuid,
    application.lender,
  )

  const { data: repositoryDocuments, queryKey: repositoryDocumentsQueryKey } =
    useCustomerRepositoryListSuspense(companyUuid)

  const { data: company } = useCustomerCompanyRetrieveSuspense(companyUuid)

  const { mutate: updateCompany } = useCustomerCompanyPartialUpdate({
    mutation: {
      onSuccess: (updatedCompanyData) => {
        queryClient.setQueryData(companyQueryKey, updatedCompanyData)
        queryClient.invalidateQueries({
          queryKey: applicationQueryKey,
        })
      },
    },
  })

  const currentDocumentValues = new Map<
    RequiredDocumentDetails,
    RepositoryDocument
  >()

  const handleBankAccountsCountSelection = (count: number) => {
    updateCompany({
      uuid: companyUuid,
      data: { bankAccountsCount: count },
    })
  }

  const requiredDocuments = lender.requiredDocuments

  const filteredRequiredDocuments = requiredDocuments
    .filter((doc) => {
      if (doc.category !== CategoryEnum.banking) return true
      return (
        doc.referenceAccount !== null &&
        doc.referenceAccount <= (company.bankAccountsCount ?? 1)
      )
    })
    .sort((a, b) => {
      // Non-banking always on top
      if (
        a.category !== CategoryEnum.banking &&
        b.category === CategoryEnum.banking
      )
        return -1
      if (
        a.category === CategoryEnum.banking &&
        b.category !== CategoryEnum.banking
      )
        return 1

      // Both non-banking → sort by `order` field
      if (
        a.category !== CategoryEnum.banking &&
        b.category !== CategoryEnum.banking
      ) {
        const aOrder = a.order ?? Number.POSITIVE_INFINITY
        const bOrder = b.order ?? Number.POSITIVE_INFINITY
        return aOrder - bOrder
      }

      // Both banking → sort by referenceAccount
      if (a.referenceAccount !== b.referenceAccount) {
        return (
          (a.referenceAccount ?? Infinity) - (b.referenceAccount ?? Infinity)
        )
      }

      // Same account → sort alphabetically by name (months will line up)
      return a.name.localeCompare(b.name)
    })

  filteredRequiredDocuments.forEach((doc) => {
    const matchingDoc = repositoryDocuments.find(
      (repoDoc) => repoDoc.type === doc.typeId,
    )
    if (matchingDoc) {
      currentDocumentValues.set(doc, matchingDoc)
    }
  })

  const nextButtonText = application.lenderConnectAccountUrl
    ? "Connect Account"
    : "Next"
  const handleNavigateNext = () => {
    if (application.lenderConnectAccountUrl) {
      window.open(application.lenderConnectAccountUrl, "_blank")
    }
    flowActorRef.send({ type: "next" })
  }

  const ExtraDataForm = getExtraDataFormComponent(
    application.referralIntegration,
  )

  const canGoNext = ApplicationFlowContext.useSelector((snapshot) => {
    return snapshot.can({ type: "proceedNext" })
  })

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      <Card className="col-span-1 lg:col-span-2">
        <CardHeader>
          <CardTitle>Upload Required Documents</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-col pt-2 sm:pt-5 sm:pr-3">
              <h2 className="text-foreground text-sm font-semibold">
                How many business bank accounts do you actively use that hold
                cash balance and send/receive transactions?
              </h2>
              <p className="text-xs text-gray-500">
                Count only accounts where revenue actually lands; exclude
                personal, savings/yield/reserve or sweep-only accounts.
              </p>
            </div>
            <div className="flex flex-wrap gap-2 sm:gap-3">
              {[1, 2, 3, 4, 5].map((count) => (
                <Button
                  key={count}
                  variant={
                    company.bankAccountsCount === count ? "default" : "outline"
                  }
                  size="sm"
                  onClick={() => handleBankAccountsCountSelection(count)}
                  className={`transition-smooth min-w-16 ${
                    company.bankAccountsCount === count
                      ? "bg-primary text-primary-foreground"
                      : "hover:bg-accent"
                  }`}
                >
                  {count}
                </Button>
              ))}
            </div>
          </div>
          <RequiredDocumentsForm
            companyUuid={companyUuid}
            requiredDocuments={filteredRequiredDocuments}
            existingRepositoryDocuments={repositoryDocuments}
            onSuccess={() => {
              // Invalidate the application data to refresh the hasAllRequiredDocuments
              // and filename status. For filename, we show an optimistic update early,
              // but when navigating back/forth or erroring, it should be refreshed.
              queryClient.invalidateQueries({
                queryKey: applicationQueryKey,
              })
              queryClient.invalidateQueries({
                queryKey: repositoryDocumentsQueryKey,
              })
            }}
          />
          {ExtraDataForm && (
            <>
              <Separator />
              <ExtraDataForm
                application={application}
                companyUuid={companyUuid}
              />
            </>
          )}
        </CardContent>
        <CardFooter className="flex w-full flex-col gap-2 sm:flex-row sm:justify-end">
          <ButtonBack
            className={cn(
              "w-full sm:w-auto",
              application.lenderConnectAccountUrl ? "sm:w-45" : "sm:w-35",
            )}
            onClick={() => flowActorRef.send({ type: "back" })}
          >
            Back
          </ButtonBack>

          <ButtonNext
            disabled={!canGoNext}
            className={cn(
              "w-full sm:w-auto",
              application.lenderConnectAccountUrl ? "sm:w-45" : "sm:w-35",
            )}
            onClick={handleNavigateNext}
          >
            {nextButtonText}
          </ButtonNext>
        </CardFooter>
      </Card>
    </div>
  )
}
